import { useState } from 'react';
import { Button } from '../../catalyst-components/button';
import { Input } from '../../catalyst-components/input';
import { Checkbox } from '../../catalyst-components/checkbox';
import pig1 from '../../assets/graphics/pig1.gif';
import { getClientInfo } from '../../shared/helpers/client-fingerprint';
import { useSubscribeWithIP } from '../../shared/hooks/useSubscribeWithIP';

export const Subscribe = () => {
  const [email, setEmail] = useState('');
  const [referrerEmail, setReferrerEmail] = useState('');
  const [originalUserEmail, setOriginalUserEmail] = useState(''); // Store the original user's email
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isFriend, setIsFriend] = useState(false);
  const [isAgeVerified, setIsAgeVerified] = useState(false);

  const { subscribe: subscribeUser, isLoading } = useSubscribeWithIP();

  const validateEmail = (email: string) => {
    // Validate email format with proper regex
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (!emailRegex.test(email)) {
      setError('Invalid email format');
      return false;
    }

    // Additional checks for common issues
    if (email.length > 254) {
      setError('Email address is too long');
      return false;
    }

    if (email.startsWith('.') || email.endsWith('.') ||
      email.includes('..') || email.includes('@.') ||
      email.includes('.@')) {
      setError('Invalid email format');
      return false;
    }

    // Validate TLD (top-level domain) - must have a dot followed by 2-6 letter extension
    const tldRegex = /\.[a-zA-Z]{2,6}$/;
    if (!tldRegex.test(email)) {
      setError('Email must have a valid domain extension (like .com, .org, .net)');
      return false;
    }

    // Additional check: ensure the part after @ contains at least one dot
    const domainPart = email.split('@')[1];
    if (!domainPart || !domainPart.includes('.')) {
      setError('Email must have a valid domain with extension');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      return;
    }

    try {
      if (!validateEmail(email)) {
        return;
      }

      setError('');

      // Get client information for tracking
      const clientInfo = getClientInfo();

      const result = await subscribeUser(
        email.trim().toLowerCase(),
        clientInfo,
        isFriend ? referrerEmail.trim().toLowerCase() : undefined
      );

      if (result.alreadySubscribed) {
        setMessage("You're already subscribed! We'll notify you as soon as we're ready!");
      } else {
        if (!isFriend) {
          setMessage("Welcome!   If you don't see our welcome email in your inbox, check your Spam folder and mark us \"not Spam\" to ensure you receive our app invite soon!");
          // Store the original user's email when they first sign up
          setOriginalUserEmail(email.trim().toLowerCase());
        } else {
          setMessage(`They're on our list! We'll let them know you referred them from ${originalUserEmail}`);
        }
      }

      setIsSubmitted(true);
    } catch (error) {
      setMessage("Something went wrong. Please try again later.");
      setIsSubmitted(true);
    }
  };

  return (
    <section className="flex flex-col justify-center w-full grow items-center min-h-screen">
      <div className="flex flex-col items-center max-w-lg mx-auto px-6">

        {/* Boar Animation */}
        <div className="w-72 h-72 flex items-center justify-center mb-8">
          <img
            src={pig1}
            alt="Gruntled"
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-center mb-4 text-primary-5 uppercase">
          Get Gruntled
        </h1>

        <p className="text-lg text-center mb-8 text-primary-5">
          Be first in line for holistic job search support
        </p>

        {/* Subscription Form or Success Message */}
        {!isSubmitted ? (
          <form onSubmit={handleSubmit} className="w-full max-w-sm">

            <div className="mb-4">
              <Checkbox
                checked={isAgeVerified}
                onChange={(checked) => setIsAgeVerified(checked)}
                className="mb-3"
              >
                {isFriend ? "They are at least 18 years old" : "I am at least 18 years old"}
              </Checkbox>
              
              <Input
                type="email"
                placeholder={isFriend ? "Friend's email address" : "Your email address"}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
                disabled={isLoading || !isAgeVerified}
              />
              {error && (
                <p className="text-red-700 text-left text-sm font-bold mb-4">{error}</p>
              )}
            </div>

            <Button
              type="submit"
              color="dark"
              className="w-full uppercase"
              disabled={isLoading || !email.trim() || !isAgeVerified}
            >
              {isLoading ? 'Signing Up...' : isFriend ? 'Sign Up A Friend' : 'Sign Me Up'}
            </Button>
          </form>
        ) : (
          <div className="text-center">
            {message && (
              <div className="p-6 bg-primary-2 border border-primary-4 rounded-lg">
                <p className="text-primary-5 font-medium">
                  {message}
                </p>
              </div>
            )}

            <Button
              onClick={() => {
                setIsSubmitted(false);
                // Use the original user's email as the referrer, not the friend's email
                setReferrerEmail(originalUserEmail);
                setEmail(''); // Clear email for friend's email
                setMessage('');
                setError('');
                setIsFriend(true);
                setIsAgeVerified(false); // Reset age verification for friend referral
              }}
              color="dark"
              className="mt-4 text-primary-5"
            >
              Sign Up A Friend
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}; 